import { ActionIcon, Badge, Button, Group, Paper, Stack, Text } from '@mantine/core';
import { IconEdit, IconEye, IconTrash } from '@tabler/icons-react';
import type { FormProgram, FormTemplateListItemDTO } from '@/types/formsTemplates';
import styles from './TemplateCard.module.sass';

interface TemplateCardProps {
  template: FormTemplateListItemDTO;
  onEdit: (templateId: string) => void;
  onDelete: (templateId: string) => void;
  onPreview: (templateId: string) => void;
  onToggleStatus: (templateId: string) => void;
  isEditLoading: boolean;
  isDeleteLoading: boolean;
  isStatusUpdating: boolean;
  isPreviewLoading: boolean;
}

const PROGRAM_COLORS: Record<FormProgram, string> = {
  mpa: 'blue',
  messages: 'grape',
  forms: 'teal',
  camino: 'lime',
};

const PROGRAM_LABELS: Record<FormProgram, string> = {
  mpa: 'MPA',
  messages: 'Messages',
  forms: 'Forms',
  camino: 'Camino',
};

export const TemplateCard = ({
  template,
  onEdit,
  onDelete,
  onPreview,
  onToggleStatus,
  isEditLoading,
  isDeleteLoading,
  isStatusUpdating,
  isPreviewLoading,
}: TemplateCardProps) => {
  return (
    <Paper className={styles.card} shadow="xs">
      <Group gap="xs" align="center" justify="space-between" py="sm" px="md" className={styles.statusBar}>
        <Group gap="xs" wrap="nowrap">
          <Badge variant="light" color={template.status === 'draft' ? 'yellow' : 'green'} size="sm">
            {template.status}
          </Badge>
          <Badge
            variant="light"
            color={PROGRAM_COLORS[template.program] ?? 'gray'}
            size="sm"
            className={styles.programBadge}
          >
            {PROGRAM_LABELS[template.program] ?? template.program}
          </Badge>
        </Group>
        <Group gap="xs" wrap="nowrap">
          <ActionIcon
            variant="subtle"
            size="md"
            color="gray"
            onClick={() => onEdit(template.id)}
            loading={isEditLoading}
            aria-label="Edit template"
          >
            <IconEdit size={18} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            size="md"
            color="red"
            onClick={() => onDelete(template.id)}
            loading={isDeleteLoading}
            aria-label="Delete template"
          >
            <IconTrash size={18} />
          </ActionIcon>
        </Group>
      </Group>

      <Stack gap="sm" p="md" className={styles.body}>
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text className={styles.title} lineClamp={1}>
              {template?.name || 'Untitled'}
            </Text>
            <Text size="sm" c="dimmed" lineClamp={2} className={styles.description}>
              {template?.description || 'No description'}
            </Text>
          </div>
          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
            {new Date(template.updatedDate).toLocaleDateString()}
          </Text>
        </Group>

        {template.tags.length > 0 ? (
          <Group wrap="wrap" gap="xs" align="center">
            {template.tags.map((tag) => (
              <span key={tag.id} className={styles.tagChip}>
                {tag.name}
              </span>
            ))}
          </Group>
        ) : (
          <Text size="xs" c="dimmed">
            No tags
          </Text>
        )}
      </Stack>

      <Group gap="xs" align="center" justify="space-between" p="sm" px="md" className={styles.footer}>
        <Group justify="space-between" align="center" w="100%">
          <Button
            variant="light"
            size="sm"
            leftSection={<IconEye size={16} />}
            onClick={() => onPreview(template.id)}
            loading={isPreviewLoading}
          >
            Preview
          </Button>
          <Button variant="default" size="sm" loading={isStatusUpdating} onClick={() => onToggleStatus(template.id)}>
            {template.status === 'draft' ? 'Publish' : 'Move to Draft'}
          </Button>
        </Group>
      </Group>
    </Paper>
  );
};
